import React, { useState, useEffect, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import {
  Key, Globe, Shield, Copy, RefreshCw, Trash2, Plus,
  CheckCircle2, XCircle, Eye, EyeOff, Code, BookOpen,
  ChevronDown, ChevronUp, ExternalLink, Search,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, AppBadge,
} from "../ui";
import "./PublicApiSettings.scss";

/* ── Toggle Switch ── */
function ApiToggle({ checked, onChange, id, disabled }) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={`papi-toggle ${checked ? "papi-toggle--on" : ""} ${disabled ? "papi-toggle--disabled" : ""}`}
      onClick={() => !disabled && onChange(!checked)}
    >
      <span className="papi-toggle__thumb" />
    </button>
  );
}

const PublicApiSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [rateLimit, setRateLimit] = useState(100);
  const [apiBaseUrl, setApiBaseUrl] = useState("");
  const [copied, setCopied] = useState("");
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [courseSearch, setCourseSearch] = useState("");
  const [showFullDocs, setShowFullDocs] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  const selectedCourseObj = useMemo(
    () => courses.find((c) => (c._id || c.id) === selectedCourse),
    [courses, selectedCourse]
  );

  // Normalize course ID (API returns `id`, some may have `_id`)
  const getCourseId = (c) => c?._id || c?.id || "";
  const selectedCourseId = getCourseId(selectedCourseObj);

  const filteredCourses = useMemo(() => {
    if (!courseSearch.trim()) return courses;
    const q = courseSearch.toLowerCase();
    return courses.filter((c) => c.name.toLowerCase().includes(q));
  }, [courses, courseSearch]);

  useEffect(() => {
    loadSettings();
    loadCourses();
    // eslint-disable-next-line
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/institute/public-api-settings");
      const pa = data.publicApi || {};
      setEnabled(pa.enabled || false);
      setApiKey(pa.apiKey || "");
      setAllowedDomains(pa.allowedDomains || []);
      setRateLimit(pa.rateLimit || 100);
      setApiBaseUrl(data.apiBaseUrl || "");
    } catch {
      showToast("Failed to load API settings", "error");
    }
    setLoading(false);
  };

  const loadCourses = async () => {
    try {
      const { data } = await apiClient.get("/courses/names");
      setCourses(data.courses || data || []);
    } catch {
      // non-critical — course explorer just won't populate
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put("/institute/public-api-settings", {
        enabled,
        allowedDomains,
        rateLimit,
      });
      showToast("API settings saved successfully!");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save settings", "error");
    }
    setSaving(false);
  };

  const handleGenerateKey = async () => {
    setGenerating(true);
    try {
      const { data } = await apiClient.post("/institute/public-api-settings/generate-key");
      setApiKey(data.apiKey);
      setShowApiKey(true);
      showToast("API key generated! Save it securely — it won't be shown again in full.");
    } catch {
      showToast("Failed to generate API key", "error");
    }
    setGenerating(false);
  };

  const handleRevokeKey = async () => {
    if (!window.confirm("Are you sure you want to revoke the API key? This will immediately break any integrations using it.")) return;
    try {
      await apiClient.delete("/institute/public-api-settings/revoke-key");
      setApiKey("");
      showToast("API key revoked.");
    } catch {
      showToast("Failed to revoke API key", "error");
    }
  };

  const handleAddDomain = () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    if (!/^[a-z0-9]([a-z0-9.-]*[a-z0-9])?(\.[a-z]{2,})+$/.test(domain)) {
      showToast("Invalid domain format. Example: www.myinstitute.com", "error");
      return;
    }
    if (allowedDomains.includes(domain)) {
      showToast("Domain already added", "error");
      return;
    }
    setAllowedDomains([...allowedDomains, domain]);
    setNewDomain("");
  };

  const handleRemoveDomain = (domain) => {
    setAllowedDomains(allowedDomains.filter((d) => d !== domain));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(""), 2000);
  };

  const maskedKey = apiKey
    ? `${apiKey.substring(0, 12)}${"•".repeat(20)}${apiKey.substring(apiKey.length - 6)}`
    : "";

  if (loading) {
    return (
      <div className="papi-loading">
        <Spinner animation="border" size="sm" /> Loading API settings…
      </div>
    );
  }

  return (
    <div className="papi-settings">
      {/* Enable/Disable */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="papi-card-title">
              <Shield size={18} /> Public API Access
            </span>
          </AppCardTitle>
          <p className="papi-card-desc">
            Enable public API access to let your website fetch course data directly from your LMS.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="papi-toggle-row">
            <div className="papi-toggle-info">
              <span className="papi-toggle-label">Enable Public API</span>
              <span className="papi-toggle-hint">
                When enabled, external websites can access your course catalog via API.
              </span>
            </div>
            <ApiToggle checked={enabled} onChange={setEnabled} id="papi-enabled" />
          </div>
          {enabled && (
            <div className="papi-status-badge">
              <AppBadge variant="success">
                <CheckCircle2 size={12} /> API Active
              </AppBadge>
            </div>
          )}
          {!enabled && (
            <div className="papi-status-badge">
              <AppBadge variant="secondary">
                <XCircle size={12} /> API Disabled
              </AppBadge>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* API Key */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="papi-card-title">
              <Key size={18} /> API Key
            </span>
          </AppCardTitle>
          <p className="papi-card-desc">
            Use this key in the <code>x-api-key</code> header when calling the API from your server.
          </p>
        </AppCardHeader>
        <AppCardContent>
          {apiKey ? (
            <div className="papi-key-display">
              <div className="papi-key-value">
                <code>{showApiKey ? apiKey : maskedKey}</code>
                <div className="papi-key-actions">
                  <button
                    className="papi-icon-btn"
                    onClick={() => setShowApiKey(!showApiKey)}
                    title={showApiKey ? "Hide" : "Show"}
                  >
                    {showApiKey ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button
                    className="papi-icon-btn"
                    onClick={() => copyToClipboard(apiKey, "apiKey")}
                    title="Copy"
                  >
                    {copied === "apiKey" ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
              <div className="papi-key-btns">
                <AppButton variant="outline" size="sm" onClick={handleGenerateKey} disabled={generating}>
                  <RefreshCw size={14} /> Regenerate
                </AppButton>
                <AppButton variant="outline" size="sm" className="papi-btn-danger" onClick={handleRevokeKey}>
                  <Trash2 size={14} /> Revoke
                </AppButton>
              </div>
            </div>
          ) : (
            <div className="papi-key-empty">
              <p>No API key generated yet.</p>
              <AppButton onClick={handleGenerateKey} disabled={generating}>
                {generating ? <Spinner animation="border" size="sm" /> : <Key size={14} />}
                Generate API Key
              </AppButton>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* Allowed Domains */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="papi-card-title">
              <Globe size={18} /> Allowed Domains
            </span>
          </AppCardTitle>
          <p className="papi-card-desc">
            Whitelist domains that can access your API without an API key (browser-based requests).
            Requests from non-whitelisted domains without an API key will be blocked.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="papi-domain-input-row">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="e.g. www.myinstitute.com"
              className="papi-input"
              onKeyDown={(e) => e.key === "Enter" && handleAddDomain()}
            />
            <AppButton onClick={handleAddDomain} size="sm">
              <Plus size={14} /> Add
            </AppButton>
          </div>
          {allowedDomains.length > 0 ? (
            <div className="papi-domain-list">
              {allowedDomains.map((domain) => (
                <div key={domain} className="papi-domain-chip">
                  <Globe size={12} />
                  <span>{domain}</span>
                  <button
                    className="papi-domain-remove"
                    onClick={() => handleRemoveDomain(domain)}
                    title="Remove"
                  >
                    <XCircle size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="papi-domain-empty">No domains whitelisted. Only API key authentication will work.</p>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── API 1: List Courses ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="papi-card-title">
              <Code size={18} /> API 1 — List All Courses
            </span>
            <AppBadge variant="success" className="papi-method-badge">GET</AppBadge>
          </AppCardTitle>
          <p className="papi-card-desc">
            Returns all active courses with basic info (name, fee, category, thumbnail, next batch). 
            <strong> Does NOT include</strong> day-wise syllabus, full batches list, or banner images. Use this for catalog/listing pages.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="papi-apidoc">
            {/* Full URL */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Full URL</span>
              <div className="papi-code-block">
                <code>{apiBaseUrl}/courses</code>
                <button className="papi-icon-btn" onClick={() => copyToClipboard(`${apiBaseUrl}/courses`, "api1url")}>
                  {copied === "api1url" ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>

            {/* Method */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Method</span>
              <code className="papi-apidoc-value">GET</code>
            </div>

            {/* Headers */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Headers</span>
              <pre className="papi-code-example">{`{
  "x-api-key": "${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}"
}`}</pre>
            </div>

            {/* Query Parameters */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Query Parameters</span>
              <table className="papi-fulldocs-table">
                <thead>
                  <tr><th>Parameter</th><th>Type</th><th>Default</th><th>Description</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>courseType</code></td><td>string</td><td>all</td><td><code>batch</code> or <code>self-paced</code>. Omit for both.</td></tr>
                  <tr><td><code>category</code></td><td>string</td><td>—</td><td>Filter by category (case-insensitive partial match)</td></tr>
                  <tr><td><code>level</code></td><td>string</td><td>—</td><td><code>Beginner</code>, <code>Intermediate</code>, <code>Advanced</code></td></tr>
                  <tr><td><code>search</code></td><td>string</td><td>—</td><td>Search by name, title, or description</td></tr>
                  <tr><td><code>page</code></td><td>number</td><td>1</td><td>Page number</td></tr>
                  <tr><td><code>limit</code></td><td>number</td><td>50</td><td>Results per page (max 100)</td></tr>
                  <tr><td><code>sort</code></td><td>string</td><td>newest</td><td><code>newest</code>, <code>oldest</code>, <code>name_asc</code>, <code>price_asc</code></td></tr>
                </tbody>
              </table>
            </div>

            {/* Body */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Request Body</span>
              <span className="papi-apidoc-value papi-apidoc-none">None (GET request)</span>
            </div>

            {/* Response */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Response</span>
              <pre className="papi-code-example">{`{
  "success": true,
  "data": [
    {
      "id": "64a...",
      "name": "Full Stack Web Development",
      "slug": "full-stack-web-development",
      "description": "Learn HTML, CSS, JS, React & Node.js",
      "category": "Web Development",
      "level": "Beginner",
      "fee": 15000,
      "duration": "3 months",
      "courseType": "batch",
      "thumbnailUrl": "https://...",
      "nextBatch": {
        "id": "...",
        "name": "Batch #5",
        "startDate": "2026-05-01T00:00:00.000Z",
        "mode": "Online"
      }
    }
    // ... more courses
  ],
  "pagination": {
    "total": 25,
    "page": 1,
    "limit": 50,
    "pages": 1
  }
}`}</pre>
            </div>

            {/* What's NOT included */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Not Included</span>
              <div className="papi-apidoc-not-included">
                <span>Day-wise syllabus</span>
                <span>Full batch list with timings</span>
                <span>Banner image</span>

              </div>
            </div>

            {/* Full Example */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Full Example</span>
              <div className="papi-apidoc-example-wrap">
                <pre className="papi-code-example">{`// Fetch all courses
const response = await fetch('${apiBaseUrl}/courses', {
  method: 'GET',
  headers: {
    'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}'
  }
});
const { success, data, pagination } = await response.json();

if (success) {
  data.forEach(course => {
    console.log(course.name, course.fee, course.thumbnailUrl);
  });
  console.log('Total courses:', pagination.total);
}`}</pre>
                <button
                  className="papi-copy-example-btn"
                  onClick={() => copyToClipboard(`const response = await fetch('${apiBaseUrl}/courses', {\n  method: 'GET',\n  headers: {\n    'x-api-key': '${apiKey || "<your-api-key>"}'\n  }\n});\nconst { success, data, pagination } = await response.json();`, "api1example")}
                >
                  {copied === "api1example" ? <CheckCircle2 size={14} /> : <Copy size={14} />} Copy Code
                </button>
              </div>
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── API 2: Course Details (with course selector) ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="papi-card-title">
              <Search size={18} /> API 2 — Get Course Details
            </span>
            <AppBadge variant="success" className="papi-method-badge">GET</AppBadge>
          </AppCardTitle>
          <p className="papi-card-desc">
            Returns <strong>full details</strong> of a single course including day-wise syllabus, all upcoming batches with timings, and banner image. 
            <strong> Select a course below</strong> to get the exact API URL.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="papi-apidoc">
            {/* Course selector */}
            <div className="papi-apidoc-row papi-apidoc-selector">
              <span className="papi-apidoc-label">Select Course</span>
              <div className="papi-explorer-select-wrap">
                {courses.length > 8 && (
                  <input
                    type="text"
                    className="papi-input papi-explorer-search"
                    placeholder="Search courses…"
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                  />
                )}
                <select
                  className="papi-input papi-explorer-select"
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                >
                  <option value="">— Select a course to get its exact API URL —</option>
                  {filteredCourses.map((c) => (
                    <option key={getCourseId(c)} value={getCourseId(c)}>
                      {c.name}{c.slug ? ` (${c.slug})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedCourseObj ? (
              <>
                {/* Selected course info */}
                <div className="papi-explorer-course-info">
                  <AppBadge variant="info">{selectedCourseObj.name}</AppBadge>
                  {selectedCourseObj.slug && (
                    <span className="papi-explorer-slug">slug: <code>{selectedCourseObj.slug}</code></span>
                  )}
                  <span className="papi-explorer-slug">ID: <code>{selectedCourseId}</code></span>
                </div>

                {/* Full URL by ID */}
                <div className="papi-apidoc-row">
                  <span className="papi-apidoc-label">Full URL (by ID)</span>
                  <div className="papi-code-block papi-code-block--highlight">
                    <code>{apiBaseUrl}/courses/{selectedCourseId}</code>
                    <button className="papi-icon-btn" onClick={() => copyToClipboard(`${apiBaseUrl}/courses/${selectedCourseId}`, "api2urlId")}>
                      {copied === "api2urlId" ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>

                {/* Full URL by Slug */}
                {selectedCourseObj.slug && (
                  <div className="papi-apidoc-row">
                    <span className="papi-apidoc-label">Full URL (by Slug) <AppBadge variant="success" className="papi-recommended-badge">Recommended</AppBadge></span>
                    <div className="papi-code-block papi-code-block--highlight">
                      <code>{apiBaseUrl}/courses/{selectedCourseObj.slug}</code>
                      <button className="papi-icon-btn" onClick={() => copyToClipboard(`${apiBaseUrl}/courses/${selectedCourseObj.slug}`, "api2urlSlug")}>
                        {copied === "api2urlSlug" ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                    <span className="papi-docs-hint">Slug-based URLs are cleaner and SEO-friendly.</span>
                  </div>
                )}

                {/* Method */}
                <div className="papi-apidoc-row">
                  <span className="papi-apidoc-label">Method</span>
                  <code className="papi-apidoc-value">GET</code>
                </div>

                {/* Headers */}
                <div className="papi-apidoc-row">
                  <span className="papi-apidoc-label">Headers</span>
                  <pre className="papi-code-example">{`{
  "x-api-key": "${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}"
}`}</pre>
                </div>

                {/* Body */}
                <div className="papi-apidoc-row">
                  <span className="papi-apidoc-label">Request Body</span>
                  <span className="papi-apidoc-value papi-apidoc-none">None (GET request)</span>
                </div>

                {/* Response */}
                <div className="papi-apidoc-row">
                  <span className="papi-apidoc-label">Response</span>
                  <pre className="papi-code-example">{`{
  "success": true,
  "data": {
    "id": "${selectedCourseId}",
    "name": "${selectedCourseObj.name}",
    "slug": "${selectedCourseObj.slug || ""}",
    "description": "Full course description...",
    "fees": "15000",
    "duration": "3 months",
    "mode": "Classroom",
    "courseType": "batch",
    "freeDemo": "Yes",
    "currentBatch": "2026-05-01T00:00:00.000Z",
    "upcomingBatch": "2026-06-01T00:00:00.000Z",
    "thumbnailUrl": "https://...",
    "bannerUrl": "https://...",
    "dayWiseSyllabus": [
      {
        "dayNumber": 1,
        "title": "Introduction & Setup",
        "topics": ["Installation", "Hello World", "Basics"],
        "estimatedDuration": 120
      },
      {
        "dayNumber": 2,
        "title": "Core Concepts",
        "topics": ["Variables", "Data Types", "Control Flow"],
        "estimatedDuration": 120
      }
      // ... full day-wise syllabus
    ],
    "upcomingBatches": [
      {
        "id": "...",
        "name": "Weekend Batch #3",
        "startDate": "2026-05-10T00:00:00.000Z",
        "endDate": "2026-08-10T00:00:00.000Z",
        "startTime": "10:00 AM",
        "endTime": "01:00 PM",
        "mode": "Online",
        "batchType": "Weekend",
        "maxStudents": 30,
        "strength": 12
      }
    ]
  }
}`}</pre>
                </div>

                {/* What's included vs List API */}
                <div className="papi-apidoc-row">
                  <span className="papi-apidoc-label">Extra Fields (vs List API)</span>
                  <div className="papi-apidoc-included">
                    <span>Day-wise syllabus</span>
                    <span>All upcoming batches with timings</span>
                    <span>Banner image URL</span>
                    <span>Batch capacity &amp; strength</span>
                  </div>
                </div>

                {/* Full Example */}
                <div className="papi-apidoc-row">
                  <span className="papi-apidoc-label">Full Example</span>
                  <div className="papi-apidoc-example-wrap">
                    <pre className="papi-code-example">{`// Fetch full details for "${selectedCourseObj.name}"
const response = await fetch('${apiBaseUrl}/courses/${selectedCourseObj.slug || selectedCourseId}', {
  method: 'GET',
  headers: {
    'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}'
  }
});
const { success, data } = await response.json();

if (success) {
  console.log('Course:', data.name);
  console.log('Fee:', data.fees);
  console.log('Description:', data.description);
  console.log('Syllabus days:', data.dayWiseSyllabus?.length);
  console.log('Upcoming batches:', data.upcomingBatches?.length);
  
  // Render day-wise syllabus
  data.dayWiseSyllabus?.forEach(day => {
    console.log(\`Day \${day.dayNumber}: \${day.title}\`);
    day.topics?.forEach(t => console.log('  -', t));
  });
  
  // Render upcoming batches
  data.upcomingBatches?.forEach(batch => {
    console.log(\`\${batch.name} - \${batch.startDate} (\${batch.mode})\`);
  });
}`}</pre>
                    <button
                      className="papi-copy-example-btn"
                      onClick={() => copyToClipboard(`const response = await fetch('${apiBaseUrl}/courses/${selectedCourseObj.slug || selectedCourseId}', {\n  method: 'GET',\n  headers: {\n    'x-api-key': '${apiKey || "<your-api-key>"}'\n  }\n});\nconst { success, data } = await response.json();`, "api2example")}
                    >
                      {copied === "api2example" ? <CheckCircle2 size={14} /> : <Copy size={14} />} Copy Code
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="papi-explorer-placeholder">
                {courses.length > 0
                  ? "👆 Select a course above to see the exact API URL, headers, and response for that course."
                  : "No courses found. Create courses first to see API endpoints."}
              </div>
            )}
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── API 3: Submit Enquiry ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="papi-card-title">
              <Code size={18} /> API 3 — Submit Enquiry / Lead
            </span>
            <AppBadge variant="warning" className="papi-method-badge">POST</AppBadge>
          </AppCardTitle>
          <p className="papi-card-desc">
            Submit an enquiry or lead from your website. This creates a new enquiry in your LMS dashboard.
            {selectedCourseObj && <> Showing example with <strong>{selectedCourseObj.name}</strong> pre-filled.</>}
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="papi-apidoc">
            {/* Full URL */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Full URL</span>
              <div className="papi-code-block">
                <code>{apiBaseUrl}/enquiries</code>
                <button className="papi-icon-btn" onClick={() => copyToClipboard(`${apiBaseUrl}/enquiries`, "api3url")}>
                  {copied === "api3url" ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>

            {/* Method */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Method</span>
              <code className="papi-apidoc-value">POST</code>
            </div>

            {/* Headers */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Headers</span>
              <pre className="papi-code-example">{`{
  "Content-Type": "application/json",
  "x-api-key": "${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}"
}`}</pre>
            </div>

            {/* Request Body */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Request Body</span>
              <table className="papi-fulldocs-table">
                <thead>
                  <tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>name</code></td><td>string</td><td>Yes</td><td>Student's full name</td></tr>
                  <tr><td><code>email</code></td><td>string</td><td>Yes</td><td>Student's email address</td></tr>
                  <tr><td><code>phone</code></td><td>string</td><td>Yes</td><td>Student's phone number</td></tr>
                  <tr><td><code>courseId</code></td><td>string</td><td>No</td><td>Course ID or slug — links enquiry to a course</td></tr>
                  <tr><td><code>courseName</code></td><td>string</td><td>No</td><td>Course name (fuzzy matched if courseId missing)</td></tr>
                  <tr><td><code>message</code></td><td>string</td><td>No</td><td>Student's message or query</td></tr>
                  <tr><td><code>preferredBatch</code></td><td>string</td><td>No</td><td>e.g. "Weekend", "Morning", "Evening"</td></tr>
                  <tr><td><code>source</code></td><td>string</td><td>No</td><td>Lead source: "website", "landing-page", etc.</td></tr>
                </tbody>
              </table>
            </div>

            {/* Example Body */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Example Body</span>
              <pre className="papi-code-example">{`{
  "name": "Jane Doe",
  "email": "jane@example.com",
  "phone": "9876543210",
  "courseId": "${selectedCourseId || "<course-id>"}",${selectedCourseObj ? `\n  "courseName": "${selectedCourseObj.name}",` : ""}
  "message": "I am interested in this course",
  "preferredBatch": "Weekend",
  "source": "website"
}`}</pre>
            </div>

            {/* Response */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Response</span>
              <pre className="papi-code-example">{`{
  "success": true,
  "data": {
    "id": "...",
    "message": "Enquiry submitted successfully"
  }
}`}</pre>
            </div>

            {/* Full Example */}
            <div className="papi-apidoc-row">
              <span className="papi-apidoc-label">Full Example</span>
              <div className="papi-apidoc-example-wrap">
                <pre className="papi-code-example">{`// Submit enquiry${selectedCourseObj ? ` for "${selectedCourseObj.name}"` : ""}
const response = await fetch('${apiBaseUrl}/enquiries', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}'
  },
  body: JSON.stringify({
    name: 'Jane Doe',
    email: 'jane@example.com',
    phone: '9876543210',
    courseId: '${selectedCourseId || "<course-id>"}',
    message: 'I am interested${selectedCourseObj ? ` in ${selectedCourseObj.name}` : ""}',
    source: 'website'
  })
});
const result = await response.json();

if (result.success) {
  alert('Thank you! We will contact you soon.');
}`}</pre>
                <button
                  className="papi-copy-example-btn"
                  onClick={() => copyToClipboard(`const response = await fetch('${apiBaseUrl}/enquiries', {\n  method: 'POST',\n  headers: {\n    'Content-Type': 'application/json',\n    'x-api-key': '${apiKey || "<your-api-key>"}'\n  },\n  body: JSON.stringify({\n    name: 'Jane Doe',\n    email: 'jane@example.com',\n    phone: '9876543210',\n    courseId: '${selectedCourseId || "<course-id>"}',\n    message: 'I am interested',\n    source: 'website'\n  })\n});\nconst result = await response.json();`, "api3example")}
                >
                  {copied === "api3example" ? <CheckCircle2 size={14} /> : <Copy size={14} />} Copy Code
                </button>
              </div>
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── Full API Documentation ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="papi-card-title">
              <BookOpen size={18} /> Full API Documentation
            </span>
          </AppCardTitle>
          <p className="papi-card-desc">
            Complete API reference with all endpoints, request/response schemas, error codes, and integration guide.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <button
            className="papi-fulldocs-toggle"
            onClick={() => setShowFullDocs(!showFullDocs)}
          >
            {showFullDocs ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {showFullDocs ? "Collapse Documentation" : "View Full API Documentation"}
            <ExternalLink size={14} />
          </button>

          {showFullDocs && (
            <div className="papi-fulldocs">
              {/* ── Overview ── */}
              <div className="papi-fulldocs-section">
                <h5>Overview</h5>
                <p>The Public API allows your website to fetch course data, display course catalogs, and submit enquiries/leads programmatically. All requests go through <code>{apiBaseUrl}</code>.</p>
              </div>

              {/* ── Authentication ── */}
              <div className="papi-fulldocs-section">
                <h5>Authentication</h5>
                <p>There are two ways to authenticate:</p>
                <table className="papi-fulldocs-table">
                  <thead>
                    <tr><th>Method</th><th>How</th><th>Best For</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><code>x-api-key</code> header</td>
                      <td>Pass your API key in the <code>x-api-key</code> HTTP header</td>
                      <td>Server-side calls (Node.js, PHP, Python, etc.)</td>
                    </tr>
                    <tr>
                      <td>Domain Whitelist</td>
                      <td>Add your domain to the allowed domains list above</td>
                      <td>Browser-based JavaScript calls (no API key exposed)</td>
                    </tr>
                  </tbody>
                </table>
                <pre className="papi-code-example">{`// Server-side (recommended)
headers: { 'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}' }

// Browser-side (domain must be whitelisted above)
// No header needed — origin is verified automatically`}</pre>
              </div>

              {/* ── Rate Limiting ── */}
              <div className="papi-fulldocs-section">
                <h5>Rate Limiting</h5>
                <p>API requests are limited to <strong>{rateLimit} requests per minute</strong> per API key. If exceeded, you'll receive a <code>429 Too Many Requests</code> response. Implement caching on your end to reduce API calls.</p>
              </div>

              {/* ── Response Format ── */}
              <div className="papi-fulldocs-section">
                <h5>Response Format</h5>
                <p>All responses follow a consistent JSON structure:</p>
                <pre className="papi-code-example">{`// Successful response
{
  "success": true,
  "data": { ... } or [ ... ],
  "pagination": {       // Only for list endpoints
    "total": 50,
    "page": 1,
    "limit": 20,
    "pages": 3
  }
}

// Error response
{
  "success": false,
  "error": "ERROR_CODE",
  "message": "Human-readable error description"
}`}</pre>
              </div>

              {/* ── Error Codes ── */}
              <div className="papi-fulldocs-section">
                <h5>Error Codes</h5>
                <table className="papi-fulldocs-table">
                  <thead>
                    <tr><th>HTTP Status</th><th>Error Code</th><th>Description</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>400</td><td><code>VALIDATION_ERROR</code></td><td>Invalid or missing required fields</td></tr>
                    <tr><td>401</td><td><code>UNAUTHORIZED</code></td><td>Missing or invalid API key / domain not whitelisted</td></tr>
                    <tr><td>404</td><td><code>NOT_FOUND</code></td><td>Course or resource not found</td></tr>
                    <tr><td>429</td><td><code>RATE_LIMIT_EXCEEDED</code></td><td>Too many requests — try again later</td></tr>
                    <tr><td>500</td><td><code>INTERNAL_ERROR</code></td><td>Server error — contact support</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="papi-fulldocs-divider" />

              {/* ── Endpoint 1: List Courses ── */}
              <div className="papi-fulldocs-section">
                <h5>
                  <AppBadge variant="success" className="papi-method-badge">GET</AppBadge>
                  /courses
                </h5>
                <p>List all active courses for your institute. Supports filtering, searching, sorting, and pagination.</p>
                <h6>Query Parameters</h6>
                <table className="papi-fulldocs-table">
                  <thead>
                    <tr><th>Parameter</th><th>Type</th><th>Default</th><th>Description</th></tr>
                  </thead>
                  <tbody>
                    <tr><td><code>courseType</code></td><td>string</td><td>all</td><td><code>batch</code> or <code>self-paced</code>. Omit for both.</td></tr>
                    <tr><td><code>category</code></td><td>string</td><td>—</td><td>Filter by category name (case-insensitive partial match)</td></tr>
                    <tr><td><code>level</code></td><td>string</td><td>—</td><td>Filter by level (e.g., Beginner, Intermediate, Advanced)</td></tr>
                    <tr><td><code>search</code></td><td>string</td><td>—</td><td>Search by course name, title, or description</td></tr>
                    <tr><td><code>page</code></td><td>number</td><td>1</td><td>Page number for pagination</td></tr>
                    <tr><td><code>limit</code></td><td>number</td><td>50</td><td>Results per page (max 100)</td></tr>
                    <tr><td><code>sort</code></td><td>string</td><td>newest</td><td><code>newest</code>, <code>oldest</code>, <code>name_asc</code>, <code>price_asc</code></td></tr>
                  </tbody>
                </table>
                <h6>Example Request</h6>
                <pre className="papi-code-example">{`GET ${apiBaseUrl}/courses?courseType=batch&category=Web Development&page=1&limit=10&sort=newest`}</pre>
                <h6>Example Response</h6>
                <pre className="papi-code-example">{`{
  "success": true,
  "data": [
    {
      "id": "64a...",
      "name": "Full Stack Web Development",
      "slug": "full-stack-web-development",
      "description": "Learn HTML, CSS, JS, React & Node.js",
      "category": "Web Development",
      "level": "Beginner",
      "fee": 15000,
      "duration": "3 months",
      "courseType": "batch",
      "thumbnailUrl": "https://...",
      "nextBatch": {
        "id": "...",
        "name": "Batch #5",
        "startDate": "2026-05-01T00:00:00.000Z",
        "mode": "Online"
      }
    }
  ],
  "pagination": { "total": 25, "page": 1, "limit": 10, "pages": 3 }
}`}</pre>
              </div>

              <div className="papi-fulldocs-divider" />

              {/* ── Endpoint 2: Course Names ── */}
              <div className="papi-fulldocs-section">
                <h5>
                  <AppBadge variant="success" className="papi-method-badge">GET</AppBadge>
                  /courses/list/names
                </h5>
                <p>Lightweight endpoint returning only course ID, name, and slug. Ideal for populating dropdown selectors on enquiry forms.</p>
                <h6>Example Response</h6>
                <pre className="papi-code-example">{`{
  "success": true,
  "data": [
    { "id": "64a...", "name": "Full Stack Web Dev", "slug": "full-stack-web-dev" },
    { "id": "64b...", "name": "Data Science Bootcamp", "slug": "data-science-bootcamp" }
  ]
}`}</pre>
              </div>

              <div className="papi-fulldocs-divider" />

              {/* ── Endpoint 3: Course Details ── */}
              <div className="papi-fulldocs-section">
                <h5>
                  <AppBadge variant="success" className="papi-method-badge">GET</AppBadge>
                  /courses/:idOrSlug
                </h5>
                <p>Get full details for a single course. You can pass either the course ID or the URL slug.</p>
                <h6>Path Parameters</h6>
                <table className="papi-fulldocs-table">
                  <thead>
                    <tr><th>Parameter</th><th>Type</th><th>Description</th></tr>
                  </thead>
                  <tbody>
                    <tr><td><code>idOrSlug</code></td><td>string</td><td>Course ID (MongoDB ObjectId) or URL slug</td></tr>
                  </tbody>
                </table>
                <h6>Example Response</h6>
                <pre className="papi-code-example">{`{
  "success": true,
  "data": {
    "id": "64a...",
    "name": "Full Stack Web Development",
    "slug": "full-stack-web-development",
    "description": "Complete course covering...",
    "fees": "15000",
    "duration": "3 months",
    "mode": "Classroom",
    "courseType": "batch",
    "thumbnailUrl": "https://...",
    "bannerUrl": "https://...",
    "dayWiseSyllabus": [
      { "dayNumber": 1, "title": "Setup & Intro", "topics": [...], "estimatedDuration": 120 },
      { "dayNumber": 2, "title": "Core Concepts", "topics": [...], "estimatedDuration": 120 }
    ],
    "upcomingBatches": [
      {
        "id": "...",
        "name": "Weekend Batch #3",
        "startDate": "2026-05-10T00:00:00.000Z",
        "endDate": "2026-08-10T00:00:00.000Z",
        "startTime": "10:00 AM",
        "endTime": "01:00 PM",
        "mode": "Online"
      }
    ]
  }
}`}</pre>
              </div>

              <div className="papi-fulldocs-divider" />

              {/* ── Endpoint 4: Submit Enquiry ── */}
              <div className="papi-fulldocs-section">
                <h5>
                  <AppBadge variant="warning" className="papi-method-badge">POST</AppBadge>
                  /enquiries
                </h5>
                <p>Submit an enquiry or lead — this creates a new enquiry in your LMS dashboard.</p>
                <h6>Request Body</h6>
                <table className="papi-fulldocs-table">
                  <thead>
                    <tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr>
                  </thead>
                  <tbody>
                    <tr><td><code>name</code></td><td>string</td><td>Yes</td><td>Student's full name</td></tr>
                    <tr><td><code>email</code></td><td>string</td><td>Yes</td><td>Student's email address</td></tr>
                    <tr><td><code>phone</code></td><td>string</td><td>Yes</td><td>Student's phone number</td></tr>
                    <tr><td><code>courseId</code></td><td>string</td><td>No</td><td>Course ID or slug (links enquiry to a specific course)</td></tr>
                    <tr><td><code>courseName</code></td><td>string</td><td>No</td><td>Course name (used for fuzzy matching if courseId is not provided)</td></tr>
                    <tr><td><code>message</code></td><td>string</td><td>No</td><td>Student's message or query</td></tr>
                    <tr><td><code>preferredBatch</code></td><td>string</td><td>No</td><td>Preferred batch timing (e.g., "Weekend", "Morning")</td></tr>
                    <tr><td><code>source</code></td><td>string</td><td>No</td><td>Lead source for tracking (e.g., "website", "landing-page")</td></tr>
                  </tbody>
                </table>
                <h6>Example Request</h6>
                <pre className="papi-code-example">{`POST ${apiBaseUrl}/enquiries
Content-Type: application/json
x-api-key: ${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}

{
  "name": "Jane Doe",
  "email": "jane@example.com",
  "phone": "9876543210",
  "courseId": "${selectedCourseId || "<course-id>"}",
  "message": "I'd like to know more about the batch timings",
  "source": "website-contact-form"
}`}</pre>
                <h6>Example Response</h6>
                <pre className="papi-code-example">{`{
  "success": true,
  "data": {
    "id": "...",
    "message": "Enquiry submitted successfully"
  }
}`}</pre>
              </div>

              <div className="papi-fulldocs-divider" />

              {/* ── Integration Guide ── */}
              <div className="papi-fulldocs-section">
                <h5>Integration Guide</h5>

                <h6>Step 1: Generate an API Key</h6>
                <p>Go to the <strong>API Key</strong> section above and generate a key. Store it securely on your server — never expose it in client-side JavaScript.</p>

                <h6>Step 2: Whitelist Your Domain (Optional)</h6>
                <p>If you need to call the API directly from the browser (e.g., a React/Next.js frontend), add your domain to the <strong>Allowed Domains</strong> list. This lets the browser make requests without exposing your API key.</p>

                <h6>Step 3: Fetch and Display Courses</h6>
                <pre className="papi-code-example">{`// Server-side (Node.js / PHP / Python) — use API key
const response = await fetch('${apiBaseUrl}/courses', {
  headers: { 'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}' }
});
const { data, pagination } = await response.json();

// Render courses on your website
data.forEach(course => {
  console.log(course.name, course.fee, course.thumbnailUrl);
});`}</pre>

                <h6>Step 4: Build a Course Detail Page</h6>
                <pre className="papi-code-example">{`// Fetch single course by slug (clean URL)
const res = await fetch('${apiBaseUrl}/courses/your-course-slug', {
  headers: { 'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}' }
});
const { data: course } = await res.json();

// Display: course.name, course.description, course.fee,
//          course.bannerUrl, course.upcomingBatches, etc.`}</pre>

                <h6>Step 5: Add an Enquiry Form</h6>
                <pre className="papi-code-example">{`// When user submits your contact form:
const res = await fetch('${apiBaseUrl}/enquiries', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': '${apiKey ? apiKey.substring(0, 12) + "..." : "<your-api-key>"}'
  },
  body: JSON.stringify({
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    courseId: selectedCourseId, // from your course dropdown
    message: formData.message,
    source: 'website'
  })
});
const result = await res.json();
if (result.success) {
  alert('Thank you! We will contact you soon.');
}`}</pre>
              </div>

              {/* ── CORS Note ── */}
              <div className="papi-fulldocs-section">
                <h5>CORS &amp; Security Notes</h5>
                <ul className="papi-fulldocs-list">
                  <li>Browser requests (CORS) are only allowed from <strong>whitelisted domains</strong>.</li>
                  <li>Server-side requests require the <code>x-api-key</code> header.</li>
                  <li>Never expose your API key in client-side code — use domain whitelisting for browser calls.</li>
                  <li>API responses are cached for 2 minutes for performance. Changes to courses may take up to 2 minutes to reflect.</li>
                  <li>All image URLs (thumbnails, banners) are presigned and valid for 1 hour.</li>
                </ul>
              </div>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* Save Button */}
      <div className="papi-save-row">
        <AppButton onClick={handleSave} disabled={saving}>
          {saving ? <Spinner animation="border" size="sm" /> : <Shield size={14} />}
          Save API Settings
        </AppButton>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default PublicApiSettings;
